
import {
  Component,
  OnInit,
} from '@angular/core';
import { DefaultConfiguration } from '@models/document/default-configuration.const';
import { InOutType } from '@models/generic/in-out-type.enum';
import { DocumentDto } from '@models/protocols/document.dto';
import { ProtocolDto } from '@models/protocols/protocol.dto';
import { DocumentService } from '@services/document.service';
import { MessageService } from '@services/message.service';
import { ProtocolService } from '@services/protocol.service';
import { clone } from '@utils/clone.function';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { finalize } from 'rxjs';
import { DocumentCreationDialogComponent } from './document-creation-dialog/document-creation-dialog.component';

@Component({
  selector: 'ebs-protocol',
  templateUrl: './protocol.page.html',
  styleUrls: ['./protocol.page.scss'],
})
export class ProtocolPage implements OnInit {
  public InOutType: typeof InOutType = InOutType;

  public items: MenuItem[] = [
    {
      command: () => this.openDocumentCreationDialog(),
      icon: PrimeIcons.CLOUD_UPLOAD,
      tooltip: 'Carica',
    },
    {
      command: () => this.generateNewProtocol(),
      icon: PrimeIcons.VERIFIED,
      tooltip: 'Protocolla',
    },
  ];

  public documents: DocumentDto[] = [];
  public shownDocuments: DocumentDto[] = [];

  // Paginazione esclusivamente visiva: nessuna modifica all'ordine dei documenti.
  public readonly documentsPerPage = 5;
  public documentPageFirst = 0;
  public documentResultCount = 0;

  public get documentPageEnd(): number {
    return Math.min(this.documentPageFirst + this.documentsPerPage, this.documentResultCount);
  }

  public get canGoToNextDocumentsPage(): boolean {
    return this.documentPageFirst + this.documentsPerPage < this.documentResultCount;
  }

  public onDocumentsPage(event: { first?: number }): void {
    this.documentPageFirst = event.first ?? 0;
  }

  public previousDocumentsPage(): void {
    this.documentPageFirst = Math.max(0, this.documentPageFirst - this.documentsPerPage);
  }

  public nextDocumentsPage(): void {
    if (this.canGoToNextDocumentsPage) {
      this.documentPageFirst += this.documentsPerPage;
    }
  }

  public onDocumentSearchChange(): void {
    this.documentPageFirst = 0;
    this.updateDocumentResultCount();
  }

  private updateDocumentResultCount(): void {
    const term = this.searchD.trim().toLocaleLowerCase();
    this.documentResultCount = term
      ? this.shownDocuments.filter((doc) => (doc.name ?? '').toLocaleLowerCase().includes(term)).length
      : this.shownDocuments.length;
    const lastPageStart = Math.max(0, Math.ceil(this.documentResultCount / this.documentsPerPage) - 1) * this.documentsPerPage;
    this.documentPageFirst = Math.min(this.documentPageFirst, lastPageStart);
  }


  // Paginazione protocolli: 5 elementi, senza alterare l'ordinamento.
  public readonly protocolsPerPage = 5;
  public protocolPageFirst = 0;
  public protocolResultCount = 0;

  public get protocolPageEnd(): number {
    return Math.min(this.protocolPageFirst + this.protocolsPerPage, this.protocolResultCount);
  }

  public get canGoToNextProtocolsPage(): boolean {
    return this.protocolPageFirst + this.protocolsPerPage < this.protocolResultCount;
  }

  public onProtocolsPage(event: { first?: number }): void {
    this.protocolPageFirst = event.first ?? 0;
  }

  public previousProtocolsPage(): void {
    this.protocolPageFirst = Math.max(0, this.protocolPageFirst - this.protocolsPerPage);
  }

  public nextProtocolsPage(): void {
    if (this.canGoToNextProtocolsPage) this.protocolPageFirst += this.protocolsPerPage;
  }

  public onProtocolSearchChange(): void {
    this.protocolPageFirst = 0;
    this.updateProtocolResultCount();
  }

  private updateProtocolResultCount(): void {
    const term = this.searchP.trim().toLocaleLowerCase();
    this.protocolResultCount = term
      ? this.shownProtocols.filter((pr) =>
          String(pr.protocolId ?? '').toLocaleLowerCase().includes(term) ||
          (pr.document?.name ?? '').toLocaleLowerCase().includes(term)
        ).length
      : this.shownProtocols.length;
    const lastPageStart = Math.max(0, Math.ceil(this.protocolResultCount / this.protocolsPerPage) - 1) * this.protocolsPerPage;
    this.protocolPageFirst = Math.min(this.protocolPageFirst, lastPageStart);
  }

  public get latest(): ProtocolDto | undefined {
    return this.protocols
      .sort((a, b) =>
        a.protocolId.localeCompare(b.protocolId) === 1 ? -1 : 1,
      )
      .at(0);
  }

  public get protocolableDocuments(): DocumentDto[] {
    return this.documents
      .filter((doc) => !doc.hasProtocol)
      .sort((docA: DocumentDto, docB: DocumentDto) =>
        docA.docId! > docB.docId! ? -1 : 1,
      );
  }

  public selectedProtocolable: DocumentDto | undefined;

  public protocols: ProtocolDto[] = [];
  public shownProtocols: ProtocolDto[] = [];

  clonedProducts: { [s: number | string]: any } = {};

  years: number[] = [];
  selectedYear: number = -1;

  public searchD: string = '';
  public searchP: string = '';

  public protocolGeneration: boolean = false;
  public isOut: boolean = true;
  public showPreview: boolean = false;
  public fileSrc: any = undefined;

  // Stato del caricamento di documenti e protocolli.
  public protocolsLoading = false;
  public documentsLoading = false;

  // Contatore delle operazioni di caricamento in corso.
  private pendingLoads = 0;

  public get isLoading(): boolean {
    return this.pendingLoads > 0;
  }

  constructor(
    private protocolService: ProtocolService,
    private documentService: DocumentService,
    public dialogService: DialogService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.reloadAll();
  }

  private startLoading(): void {
    this.pendingLoads++;
  }

  private finishLoading(): void {
    this.pendingLoads = Math.max(0, this.pendingLoads - 1);
  }

  /**
   * Ricarica protocolli e documenti.
   *
   * Il caricamento rimane attivo durante entrambe
   * le richieste, anche quando vengono eseguite
   * consecutivamente.
   */
  reloadAll(): void {
    this.startLoading();
    this.protocolsLoading = true;

    this.protocolService
      .getProtocols()
      .pipe(
        finalize(() => {
          this.protocolsLoading = false;
          this.finishLoading();
        }),
      )
      .subscribe({
        next: (result) => {
          this.protocols = result;

          this.years = Array.from(
            new Set([
              ...this.protocols.map((pr) => pr.year),
              new Date().getFullYear(),
            ]),
          ).sort((a, b) => b - a);

          this.selectedYear = this.years[0];

          this.shownProtocols = this.protocols.filter(
            (pr) => pr.year === this.selectedYear,
          );
          this.protocolPageFirst = 0;
          this.updateProtocolResultCount();

          // La seconda richiesta inizia prima che
          // termini il conteggio della prima.
          this.startLoading();
          this.documentsLoading = true;

          this.documentService
            .getDocuments()
            .pipe(
              finalize(() => {
                this.documentsLoading = false;
                this.finishLoading();
              }),
            )
            .subscribe({
              next: (documents) => {
                this.documents = documents;

                this.shownDocuments = this.documents.filter(
                  (dc) =>
                    new Date(dc.createdDate!).getFullYear() ===
                    this.selectedYear,
                );
                this.updateDocumentResultCount();
              },
              error: () => {
                this.messageService.error(
                  'Documenti',
                  'Errore durante il caricamento dei documenti',
                );
              },
            });
        },
        error: () => {
          this.messageService.error(
            'Protocolli',
            'Errore durante il caricamento dei protocolli',
          );
        },
      });
  }

  onYearChange(): void {
    this.shownProtocols = this.protocols.filter(
      (pr) => pr.year === this.selectedYear,
    );
    this.protocolPageFirst = 0;
    this.updateProtocolResultCount();

    this.shownDocuments = this.documents.filter(
      (dc) =>
        new Date(dc.createdDate!).getFullYear() ===
        this.selectedYear,
    );
    this.documentPageFirst = 0;
    this.updateDocumentResultCount();
  }

  previewFile(doc: DocumentDto): void {
    this.documentService
      .downloadFile(doc)
      .subscribe(async (result: any) => {
        this.fileSrc = result;
        this.showPreview = true;
      });
  }

  closePreview(): void {
    this.fileSrc = undefined;
    this.showPreview = false;
  }

  downloadFile(
    doc: DocumentDto,
    mustProtocol: boolean = false,
    protocol?: ProtocolDto,
  ): void {
    this.documentService
      .downloadFile(doc)
      .subscribe(async (result: any) => {
        if (mustProtocol) {
          let blob = await this.documentService.writePdfFromBuffer(
            await (<Blob>result).arrayBuffer(),
            DefaultConfiguration,
            {
              protocol: `Protocollo N. ${
                protocol!.protocolId
              } del ${new Date().toLocaleDateString(
                'it-IT',
                {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                },
              )}`,
              orgName:
                'Segreteria Provinciale FAISA-CISAL di Pistoia',
              type: `${
                protocol!.type == InOutType.IN ? 'E' : 'U'
              }`,
            },
          );

          this.documentService
            .uploadFile(doc, blob)
            .subscribe((res) => {
              this.downloadFile(doc);
            });

          this.reloadAll();
        } else {
          let downloadLink = document.createElement('a');

          downloadLink.href =
            window.URL.createObjectURL(result);

          if (doc.name) {
            downloadLink.setAttribute(
              'download',
              doc.name,
            );
          }

          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        }
      });
  }

  onProtocolRowEditInit(element: ProtocolDto): void {
    this.clonedProducts[element.protocolId] = clone(element);
  }

  onProtocolRowEditSave(element: ProtocolDto): void {
    delete this.clonedProducts[element.protocolId];
  }

  onProtocolRowEditCancel(
    element: ProtocolDto,
    index: number,
  ): void {
    this.protocols[index] =
      this.clonedProducts[element.protocolId];

    delete this.clonedProducts[element.protocolId];
  }

  onProtocolDelete(element: ProtocolDto): void {
    this.protocolService
      .removeProtocol([element])
      .subscribe((result) => {
        this.reloadAll();
      });
  }

  onDocumentRowEditInit(element: DocumentDto): void {
    this.clonedProducts[element.docId || -1] =
      clone(element);
  }

  onDocumentRowEditSave(element: DocumentDto): void {
    delete this.clonedProducts[element.docId || -1];

    this.documentService
      .modifyDocumentMetadata([element])
      .subscribe((result) => {
        this.reloadAll();
      });
  }

  onDocumentDelete(element: DocumentDto): void {
    // Mantiene visibile il loading durante l'eliminazione
    // e fino al completamento del successivo aggiornamento.
    this.startLoading();

    this.documentService
      .removeDocument([element])
      .pipe(finalize(() => this.finishLoading()))
      .subscribe({
        next: () => {
          // reloadAll avvia un proprio caricamento prima che
          // finalize termini quello dell'eliminazione.
          this.reloadAll();
        },
        error: () => {
          this.messageService.error(
            'Documenti',
            'Errore durante l’eliminazione del documento',
          );
        },
      });
  }

  onDocumentRowEditCancel(
    element: DocumentDto,
    index: number,
  ): void {
    this.documents[index] =
      this.clonedProducts[element.docId || -1];

    delete this.clonedProducts[element.docId || -1];
  }

  openDocumentCreationDialog(
    document: DocumentDto | undefined = undefined,
  ): void {
    let dialogRef = this.dialogService.open(
      DocumentCreationDialogComponent,
      {
        closable: true,
        closeOnEscape: false,
        header: 'Gestione documentale',
        data: {
          document,
        },
      },
    );

    dialogRef.onClose.subscribe(() => {
      // Dopo il caricamento/gestione di un documento, mostra la stessa
      // immagine di loading finché documenti e protocolli sono aggiornati.
      this.reloadAll();
    });
  }

  generateNewProtocol(): void {
    this.protocolGeneration = true;
  }

  closeProtocolGeneration(): void {
    this.protocolGeneration = false;
    this.selectedProtocolable = undefined;
    this.isOut = false;
  }

  downloadAll(): void {
    for (let doc of this.documents) {
      this.downloadFile(doc);
    }
  }

  confirm(): void {
    if (this.selectedProtocolable !== undefined) {
      this.protocolService
        .createProtocol([
          {
            document: this.selectedProtocolable.docId!,
            type: this.isOut
              ? InOutType.OUT
              : InOutType.IN,
          },
        ])
        .subscribe((result) => {
          this.downloadFile(
            this.selectedProtocolable!,
            true,
            result,
          );

          this.reloadAll();
          this.closeProtocolGeneration();
        });
    }
  }

  cancel(): void {
    this.closeProtocolGeneration();
  }
}
